import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { FiEye, FiEyeOff } from 'react-icons/fi'
import { closeConnectionModal, fetchAllPublicKeys } from '../../integrationsSlice'
import { fetchAllSecrets } from '../../../../features/aws/awsSecretsManagerSlice'
import { baseUrl } from '../../../../modules/urls'
import { getAxiosDefaultConfig } from '../../../../modules/utils'
import { parseIntegrationTags } from '../../integrationTags'

const openaiAdsConnectUrl = `${baseUrl}/auth/openai-ads/connect`

interface FormValues {
  api_key: string
}

interface Channel {
  id: string
  ppKeys?: Array<{ keyId: string; keyValue: string; tags?: unknown }>
}

interface Props {
  ownerId: string | number
  retrieveAccounts: () => Promise<void>
  channel?: Channel
  allPublicKeys?: Array<{ keyId: string; keyValue?: string; tags?: unknown }>
}

type ViewMode = 'manage' | 'connect' | 'update'

function isOpenAiAdsKeyId(keyId: string | undefined | null): boolean {
  if (keyId == null || keyId === '') return false
  return String(keyId).endsWith('--openai_ads')
}

function collectRows(
  channel: Channel | undefined,
  allPublicKeys: Array<{ keyId: string; tags?: unknown }> | undefined
): Array<{ keyId: string; apiKeyPreview: string; adAccountName: string; adAccountId: string }> {
  const map = new Map<string, { keyId: string; tags?: unknown }>()
  ;(allPublicKeys || []).forEach((row) => {
    if (!row?.keyId || !isOpenAiAdsKeyId(row.keyId)) return
    map.set(row.keyId, row)
  })
  ;(channel?.ppKeys || []).forEach((row: any) => {
    if (row?.keyId && isOpenAiAdsKeyId(row.keyId) && !map.has(row.keyId)) {
      map.set(row.keyId, row)
    }
  })
  return Array.from(map.values()).map((row) => {
    const tm = parseIntegrationTags(row.tags)
    return {
      keyId: row.keyId,
      apiKeyPreview: String(tm.openai_ads_api_key_preview || ''),
      adAccountName: String(tm.ad_account_name || ''),
      adAccountId: String(tm.ad_account_id || ''),
    }
  })
}

export default function OpenAIAdsConnectionModalContent({
  ownerId,
  retrieveAccounts,
  channel,
  allPublicKeys,
}: Props) {
  const dispatch = useDispatch<any>()
  const closeModal = () => dispatch((closeConnectionModal as any)())

  const rows = useMemo(() => collectRows(channel, allPublicKeys), [channel, allPublicKeys])
  const hasConnections = rows.length > 0

  const [viewMode, setViewMode] = useState<ViewMode>(hasConnections ? 'manage' : 'connect')
  const [showApiKey, setShowApiKey] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)

  useEffect(() => {
    setViewMode((cur) => {
      if (hasConnections && cur === 'connect') return 'manage'
      if (!hasConnections && cur === 'manage') return 'connect'
      return cur
    })
  }, [hasConnections])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { api_key: '' },
  })

  const onSubmit = async ({ api_key }: FormValues) => {
    setConnectError(null)
    const isUpdate = viewMode === 'update'
    try {
      await axios.post(
        openaiAdsConnectUrl,
        { api_key: api_key.trim(), ...(isUpdate ? { update: true } : {}) },
        getAxiosDefaultConfig()
      )
      await retrieveAccounts()
      if (ownerId) {
        dispatch((fetchAllPublicKeys as any)({ ownerId }))
        dispatch((fetchAllSecrets as any)({ ownerId }))
      }
      reset()
      setViewMode('manage')
      dispatch((closeConnectionModal as any)())
    } catch (err: any) {
      setConnectError(
        err?.response?.data?.error ||
          'Failed to connect OpenAI Ads. Check your Ads Manager API key and try again.'
      )
    }
  }

  const credentialForm = (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4 tw-mb-5 tw-text-sm tw-text-blue-900">
        <p className="tw-mb-2 tw-font-semibold">OpenAI Ads API key</p>
        <p className="tw-mb-2">
          Create an ad-account key in ChatGPT Ads Manager → Settings. PayHelm uses it only to call{' '}
          <code className="tw-text-xs">api.ads.openai.com</code> for campaign, audience, conversion tracking (pixels, Conversions API, event settings), spending limit, bulk, and insights tools.
        </p>
        <p className="tw-mb-0">
          See the{' '}
          <a
            href="https://developers.openai.com/ads/api-quickstart"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-text-blue-600 tw-underline hover:tw-text-blue-800"
          >
            OpenAI Ads API quickstart
            <FontAwesomeIcon icon={faExternalLinkAlt} className="tw-ml-1 tw-text-xs" />
          </a>
          .
        </p>
      </div>
      <div className="tw-mb-4">
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">API key</label>
        <div className="tw-flex tw-items-center tw-gap-2">
          <input
            {...register('api_key', {
              required: 'API key is required',
              validate: (v) => v.trim().length > 0 || 'API key cannot be empty',
            })}
            type={showApiKey ? 'text' : 'password'}
            autoComplete="off"
            placeholder="Paste your OpenAI Ads API key"
            className="tw-flex-1 tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
          />
          <button
            type="button"
            className="tw-text-gray-500 hover:tw-text-gray-800 tw-text-lg"
            onClick={() => setShowApiKey((s) => !s)}
            aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
          >
            {showApiKey ? <FiEyeOff /> : <FiEye />}
          </button>
        </div>
        {errors.api_key && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.api_key.message}</p>}
      </div>
      {connectError && (
        <div className="tw-bg-red-50 tw-border tw-border-red-300 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-red-700 tw-mb-4">
          {connectError}
        </div>
      )}
      <div className="tw-flex tw-items-center tw-justify-end tw-space-x-2">
        <Button
          variant="secondary"
          onClick={() => {
            setConnectError(null)
            reset()
            if (viewMode === 'update' || hasConnections) setViewMode('manage')
            else closeModal()
          }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="tw-mr-2" />
              {viewMode === 'update' ? 'Saving…' : 'Connecting…'}
            </>
          ) : viewMode === 'update' ? (
            'Save API key'
          ) : (
            'Connect'
          )}
        </Button>
      </div>
    </form>
  )

  if (viewMode === 'manage' && hasConnections) {
    const row = rows[0]
    return (
      <div className="tw-p-6">
        <h2 className="tw-text-xl tw-mb-4">Manage OpenAI Ads</h2>
        <div className="tw-p-4 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-mb-6">
          <p className="tw-font-medium tw-text-gray-900">Connected</p>
          {row.adAccountName ? (
            <p className="tw-text-sm tw-text-gray-600">{row.adAccountName}</p>
          ) : null}
          {row.adAccountId ? (
            <p className="tw-text-sm tw-text-gray-600 tw-font-mono">{row.adAccountId}</p>
          ) : null}
          <p className="tw-text-sm tw-text-gray-600">
            API key: <span className="tw-font-mono">{row.apiKeyPreview || '—'}</span>
          </p>
          <p className="tw-text-xs tw-text-gray-500 tw-mt-2">
            Disconnect from the integration card, or update the key below.
          </p>
        </div>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => {
              setConnectError(null)
              reset({ api_key: '' })
              setViewMode('update')
            }}
          >
            Update API key
          </Button>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="tw-p-6">
      <h2 className="tw-text-xl tw-mb-4">{viewMode === 'update' ? 'Update OpenAI Ads API key' : 'Connect OpenAI Ads'}</h2>
      {credentialForm}
    </div>
  )
}
